import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@material-ui/core";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import { CellValueChangedEvent, ColumnApi, GridApi, GridReadyEvent } from "ag-grid-community";
import { dateTimeZoneConverter } from "../../../../utils/commonFunctions/date";
import initCapitalize from "../../../../utils/commonFunctions/initCapitalize";
import useRouteParam from "../../../../hooks/useRouteParam";
import { AuthContext, UIContext } from "../../../../contexts";

import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import isEmployerRoute from "../../../../utils/commonFunctions/isEmployerRoute";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import { trackPromise } from "react-promise-tracker";
import { api } from "../../../../utils/api";
import { useEmployerRoute } from "../../../../hooks";
import ROLES from "../../../../constants/roles";
import { ENROLLER_TYPE } from "../../../../constants/enrollerType";

const EnrollerManagementGrid = (): JSX.Element => {
	const [agGridAPI, setAgGridAPI] = useState<GridApi | null>(null);
	const [agGridColumnAPI, setAgGridColumnAPI] = useState<ColumnApi | null>(null);
	const queryGroupNumber = useRouteParam("gn");
	// console.log("queryGroupNumber", queryGroupNumber);
	const navigate = useNavigate();
	const agGridRef = useRef<any>(null);
	const { setDashboardHeader, setSelectedTab } = useContext(UIContext);
	// const isEmployerRoute = useEmployerRoute();
	const { user } = useContext(AuthContext);
	const [enroller, setEnroller] = useState([]);

	const handleAgGridReady = (params: GridReadyEvent) => {
		const { api, columnApi } = params;
		setAgGridAPI(api);
		setAgGridColumnAPI(columnApi);
		api.sizeColumnsToFit();
	};
	const handleFirstDataRendered = (params: any) => null;

	const handleDateCellRenderer = ({ value }: { value: any }) => {
		const date = dateTimeZoneConverter(value as string) as string;
		return <div>{value ? date : "N/A"}</div>;
	};

	const handleUserRoleCellRenderer = ({ value }: { value: string }) => {
		const role = initCapitalize(value);
		return <div>{role}</div>;
	};

	const handleUserInformaitonClick = useCallback(
		async (_queryGroupNumber: number) => {
			const focusedCell = agGridRef?.current.api.getFocusedCell();
			const row = agGridRef?.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
			const { data } = row;
			const pathname = isEmployerRoute() ? `/employer/settings/user?gn=${_queryGroupNumber}` : "/settings/user";
			const key = "user_id";
			navigate(pathname, {
				state: {
					[key]: data._id
				}
			});
			// // console.log("isEm", isEmployerRoute);
		},
		[navigate]
	);

	const handleUserInformationCellRenderer = useCallback(
		({ queryGroupNumber }: { queryGroupNumber: number }) => {
			return (
				<Button
					className="ag-grid-cell-button"
					variant="outlined"
					onClick={() => handleUserInformaitonClick(queryGroupNumber)}
				>
					See Details
				</Button>
			);
		},
		[handleUserInformaitonClick]
	);

	const handleRegisteredCellRender = ({ value }: { value: boolean }) => {
		return <div>{value ? "Yes" : "No"}</div>;
	};

	const handleApprovedByCellRender = ({ value }: { value: any }) => {
		return <div>{typeof value === typeof {} ? value.user_name : value}</div>;
	};

	const getEnrollers = useCallback(
		async (queryGroupNumber: number) => {
			if (user === null) return;
			let _enrollers: any[] = [];
			const { type } = user;
			let enrollerQuery;
			if (type === ENROLLER_TYPE.enroller_admin) {
				enrollerQuery = {
					group_number: queryGroupNumber,
					type: ENROLLER_TYPE.enroller_admin
				};
			} else if (type === ENROLLER_TYPE.agent) {
				enrollerQuery = {
					group_number: queryGroupNumber,
					type: ENROLLER_TYPE.agent
				};
			} else {
				return;
			}

			_enrollers = await trackPromise(api.user.getEnrollers(enrollerQuery));

			if (_enrollers) {
				setEnroller(Object.assign([], _enrollers));
			}
		},
		[user]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.enroller_management);
		if (isEmployerRoute !== null && queryGroupNumber) {
			getEnrollers(queryGroupNumber as number);
		}
	}, [setDashboardHeader, getEnrollers, queryGroupNumber]);

	return (
		<div className="user-management-grid-container glb-tab-grid-container">
			<div className="ag-theme-alpine" style={{ height: "400px", width: "100%" }}>
				<AgGridReact
					ref={agGridRef}
					rowData={enroller}
					onGridReady={handleAgGridReady}
					onFirstDataRendered={handleFirstDataRendered}
					animateRows
					rowDragManaged
					frameworkComponents={{
						handleDateCellRenderer: handleDateCellRenderer,
						handleUserInformationCellRenderer: handleUserInformationCellRenderer,
						handleApprovedByCellRender: handleApprovedByCellRender,
						handleRegisteredCellRender: handleRegisteredCellRender,
						handleUserRoleCellRenderer: handleUserRoleCellRenderer
					}}
					defaultColDef={{
						filter: "agTextColumnFilter",
						floatingFilter: true,
						resizable: true,
						floatingFilterComponentParams: { suppressFilterButton: true }
					}}
					defaultColGroupDef={{ marryChildren: true }}
					rowSelection="single"
					onCellValueChanged={(params: CellValueChangedEvent) => {
						params.api.refreshCells({
							force: true,
							columns: ["assign_group", "user_information"]
						});
					}}
					enableCellChangeFlash={true}
				>
					<AgGridColumn field="user_name" headerName="User Name" suppressSizeToFit={true}></AgGridColumn>
					<AgGridColumn
						field="is_registered"
						headerName="Registered User"
						cellRenderer="handleRegisteredCellRender"
					></AgGridColumn>
					<AgGridColumn field="email" headerName="Email"></AgGridColumn>
					<AgGridColumn
						field="role"
						headerName="Role"
						cellRenderer="handleUserRoleCellRenderer"
					></AgGridColumn>
					<AgGridColumn
						field="type"
						headerName="Enroller Type"
						cellRenderer="handleUserRoleCellRenderer"
					></AgGridColumn>
					<AgGridColumn
						field="date"
						headerName="Creation Date"
						cellRenderer="handleDateCellRenderer"
					></AgGridColumn>
					<AgGridColumn
						field="last_login_date"
						headerName="Last Login Date"
						cellRenderer="handleDateCellRenderer"
					></AgGridColumn>
					<AgGridColumn
						field="user_information"
						headerName="Enroller Information"
						cellRenderer="handleUserInformationCellRenderer"
						filter={false}
						cellRendererParams={{ queryGroupNumber }}
						suppressSizeToFit={true}
					></AgGridColumn>
				</AgGridReact>
			</div>
		</div>
	);
};

export default EnrollerManagementGrid;
